import { useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import { ArrowRight, BarChart3, DollarSign, Gift, History, Link2, LogOut, Menu, User, X } from 'lucide-react'
import { getMobileNavItems, getNavItems, isNavItemActive, type NavItem } from './navigation'
import CabinetSwitcher from './CabinetSwitcher'
import { useMobileViewport } from '../../hooks/useMobileViewport'
import { usePageScrollLock } from '../../hooks/usePageScrollLock'
import { MobileReferralProgramModal } from '../mobile-web/MobileReferralProgramModal'
import { RuStoreDownloadLink } from '../RuStoreDownloadLink'

type MobileNavigationProps = {
  role: string | null
  pathname: string
  open: boolean
  onToggle: () => void
  onClose: () => void
  onLogout: () => void
  roles: string[]
  activeRole: string
  onSwitchCabinet: (role: string) => void
}

export default function MobileNavigation({ role, pathname, open, onToggle, onClose, onLogout, roles, activeRole, onSwitchCabinet }: MobileNavigationProps) {
  usePageScrollLock(open)
  const mobileWeb = useMobileViewport()
  const [referralOpen, setReferralOpen] = useState(false)

  useEffect(() => {
    if (!mobileWeb || role !== 'manager') setReferralOpen(false)
  }, [mobileWeb, role])

  const mobileModal = mobileWeb && (
    pathname === '/profile'
    || pathname === '/saved-links'
    || pathname === '/manager/history'
    || pathname === '/manager/pricing'
    || pathname === '/manager/analytics'
    || pathname === '/driver/accept'
  )
  const navItems = mobileWeb ? getMobileNavItems(role) : getNavItems(role)
  const drawerItems: NavItem[] = mobileWeb
    ? [
        ...getMobileNavItems(role),
        ...(role === 'manager'
          ? [
              { to: '/manager/history', label: 'История перевозок', shortLabel: 'История', title: 'История', icon: History },
              { to: '/manager/pricing', label: 'Тариф и оплата', shortLabel: 'Тарифы', title: 'Тариф и оплата', icon: DollarSign },
              { to: '/manager/analytics', label: 'Аналитика', shortLabel: 'Аналитика', title: 'Аналитика', icon: BarChart3 },
            ]
          : []),
        { to: '/saved-links', label: 'Полученные по ссылке', shortLabel: 'По ссылке', title: 'По ссылке', icon: Link2 },
        { to: '/profile', label: 'Профиль', shortLabel: 'Профиль', title: 'Профиль', icon: User, exact: true },
      ]
    : navItems

  return (
    <>
      {!mobileWeb && !mobileModal && !open ? (
        <button
          type="button"
          onClick={onToggle}
          className="app-mobile-menu-button"
          title={open ? 'Закрыть меню' : 'Открыть меню'}
          aria-expanded={open}
        >
          {open ? <X className="app-mobile-menu-button__icon" /> : <Menu className="app-mobile-menu-button__icon" />}
        </button>
      ) : null}

      {!mobileModal ? <nav className="app-bottom-nav" aria-label="Мобильная навигация">
        {navItems.slice(0, 5).map((item) => {
          const Icon = item.icon
          const active = isNavItemActive(pathname, item)

          return (
            <Link
              key={item.to}
              to={item.to}
              className={`app-bottom-nav__item${active ? ' is-active' : ''}`}
              aria-current={active ? 'page' : undefined}
            >
              <Icon className="app-bottom-nav__icon" />
              <span>{item.shortLabel}</span>
            </Link>
          )
        })}
      </nav> : null}

      {open && (
        <div className="app-mobile-drawer" onClick={onClose}>
          <div className="app-mobile-drawer__panel" onClick={(event) => event.stopPropagation()}>
            <div className="app-mobile-drawer__header">
              <Link to="/landing" className="app-mobile-drawer__brand" onClick={onClose}>
                {/* White drawer on desktop widths → dark logo; the mobile web drawer is dark → white one. */}
                <img className="app-mobile-drawer__brand-logo" src={mobileWeb ? '/logolupa2.png' : '/logolupa1.png'} alt="ЛУПА" />
              </Link>
              <button type="button" onClick={onClose} className="app-mobile-drawer__close" title="Закрыть меню">
                <X className="app-mobile-drawer__close-icon" />
              </button>
            </div>

            <CabinetSwitcher roles={roles} activeRole={activeRole} onSwitch={(r) => { onSwitchCabinet(r); onClose() }} />

            <div className="app-mobile-drawer__nav">
              {drawerItems.map((item) => {
                const Icon = item.icon
                const active = isNavItemActive(pathname, item)

                return (
                  <Link
                    key={item.to}
                    to={item.to}
                    className={`app-mobile-drawer__item${active ? ' is-active' : ''}`}
                    aria-current={active ? 'page' : undefined}
                  >
                    <Icon className="app-mobile-drawer__item-icon" />
                    <span>{item.label}</span>
                  </Link>
                )
              })}
            </div>

            {mobileWeb && role === 'manager' ? (
              <button
                type="button"
                className="app-mobile-drawer__referral"
                onClick={() => {
                  onClose()
                  setReferralOpen(true)
                }}
              >
                <span className="app-mobile-drawer__referral-icon"><Gift /></span>
                <span className="app-mobile-drawer__referral-copy">
                  <strong>Пригласить коллегу</strong>
                  <small>2-я машина бесплатно навсегда</small>
                </span>
                <ArrowRight className="app-mobile-drawer__referral-arrow" />
              </button>
            ) : null}

            {mobileWeb ? <RuStoreDownloadLink className="app-mobile-drawer__download" onClick={onClose} /> : null}

            <button type="button" onClick={onLogout} className="app-mobile-drawer__logout">
              <LogOut className="app-mobile-drawer__item-icon" />
              <span>Выйти</span>
            </button>
          </div>
        </div>
      )}

      <MobileReferralProgramModal open={referralOpen && mobileWeb && role === 'manager'} onClose={() => setReferralOpen(false)} />
    </>
  )
}
